import React, { useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button } from '@mui/material';

import * as theme from '../../theme';

import { IsEnvDevelpomentOrStaging } from '../../utils/enviroment';
import { logUserAction } from '../../services/appServices';
import UserActions from '../../constants/user-actions';

// import logos
import iCal from '../../assets/iCal.png';
import Outlook from '../../assets/outlook.png';
import GoogleCalendar from '../../assets/google-calendar.png';

// import icons
import { ReactComponent as ClearIcon } from '../../assets/clear-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/calendar-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/copy-icon.svg';
import { ReactComponent as FileIcon } from '../../assets/file-icon.svg';
import { toast } from 'react-toastify';

const SubscribeDialog = (props) => {
    const { onClose, termSchoolId, timetableId, timetableType, title, isMobile } = props;

    const [copied, setCopied] = useState(false);

    //type: import or subscribe
    const generateUrl = (params) => {
        const baseUrl = IsEnvDevelpomentOrStaging()
            ? `${params.type === 'subscribe' ? 'webcal://' : 'https://'}timetablestest.bcit.ca/Calendar/Subscribe`
            : `${params.type === 'subscribe' ? 'webcal://' : 'https://'}timetablescalendar.bcit.ca/Calendar/Subscribe`;

        if (!timetableType) return 'N/A';

        if (timetableType === 'instructor')
            return baseUrl + `?termSchoolID=${termSchoolId}&instructorID=${timetableId}`;

        if (timetableType === 'set') return baseUrl + `?termSchoolID=${termSchoolId}&setID=${timetableId}`;
    };

    const handleCopyUrl = async () => {
        await window.navigator.clipboard
            .writeText(generateUrl({ type: 'import' }))
            .catch((error) => toast.error('Error copying url'));
        await logUserAction(UserActions.ICALSUBSCRIPTION, termSchoolId, timetableId, timetableType).catch((error) =>
            console.error(error)
        );
        setCopied((state) => !state);
    };

    const onSubscribe = () => { 
        const icsUrl = generateUrl({ type: 'subscribe' });

        logUserAction(UserActions.ICALSUBSCRIPTION, termSchoolId, timetableId, timetableType)
            .catch(err => console.error("Log failed:", err));

        window.location.assign(icsUrl);
    };

    const handleOnDialogClose = () => {
        setCopied(false);
        onClose();
    };

    return (
        <StyledDialog {...props} fullWidth maxWidth={isMobile ? 'sm' : 'md'}>
            <DialogContent {...props}>
                <Title>
                    <h3>
                        subscribe timetable {isMobile ? <br></br> : ` - `} {title}
                    </h3>
                    <ClearIcon onClick={handleOnDialogClose} />
                </Title>
                <Content {...props}>
                    <div>
                        <div>
                            <img src={iCal} />
                            <img src={Outlook} />
                        </div>
                        <p>
                            For <b>BCIT Outlook Calendar, Apple iCal, Microsoft Outlook 2007+</b> or any
                            iCal-compatible desktop calendar applications, you can <b>subscribe</b> to your calendar and
                            have the full timetable (including all classes and breaks) automatically added to your
                            calendar by clicking the button below:
                        </p>
                        <DialogButton variant="outlined" fullWidth onClick={onSubscribe}>
                            <CalendarIcon />
                            Subscribe to full calendar
                        </DialogButton>
                    </div>
                    <span className={isMobile ? 'horizontalLine' : 'verticalLine'}></span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div>
                            <img src={GoogleCalendar} />
                        </div>
                        <p>
                            For <b>Google Calendar</b> or other web-based calendars, and for other manual uses, you can copy and paste the link directly into your caledar application
                            (normally in the <b>Subscribe By URL</b> area):
                        </p>
                        <Link {...props}>
                            <FileIcon />
                            {generateUrl({ type: 'import' })}
                        </Link>
                        <DialogButton {...props} variant="outlined" fullWidth onClick={handleCopyUrl} disabled={copied}>
                            <CopyIcon />
                            {copied ? `Copied!` : `Copy link`}
                        </DialogButton>
                    </div>
                </Content>
                <DialogButton onClick={handleOnDialogClose} variant="contained">
                    Close
                </DialogButton>
            </DialogContent>
        </StyledDialog>
    );
};

export default SubscribeDialog;

const StyledDialog = styled(Dialog)`
    && {
        .MuiDialog-paper {
            border-radius: 10px;
        }
    }
`;

const DialogContent = styled.div`
    padding: ${(props) => (props.isMobile ? '1em' : '2em')};
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0px 10px 10px 10px;
    border-bottom: 1px solid rgba(51, 51, 51, 0.2);
    h3 {
        font-size: 16px;
        color: ${theme.colors.primary};
        text-transform: uppercase;
        font-weight: bold;
        margin: 0;
    }

    svg:hover {
        cursor: pointer;
        opacity: 0.8;
    }
`;

const Link = styled.span`
    background: rgba(0, 95, 165, 0.05);
    border-radius: 3px;
    min-height: 35px;
    overflow-x: auto;
    white-space: nowrap;
    color: ${theme.colors.primary};
    max-width: ${(props) => (props.isMobile ? '65vw' : '350px')};
    display: flex;
    align-items: center;
    padding: 0px 10px;
    svg {
        min-width: 20px;
        margin-right: 3px;
    }
    &:hover {
        cursor: default;
    }
`;

const Content = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-around;
    width: 100%;
    padding-top: 2em;
    img {
        width: 74px;
        height: auto;
        margin: 0 5px;
    }
    .verticalLine {
        border-left: 1px solid rgba(51, 51, 51, 0.2);
        height: 320px;
    }
    .horizontalLine {
        border-bottom: 1px solid rgba(51, 51, 51, 0.2);
        width: 100%;
        margin: 10px 0px;
    }
    div {
        flex: 1;
        padding: 1em 2em;
        text-align: center;
    }
    div > div {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: ${(props) => (props.isMobile ? 'unset' : '120px')};
    }
    p {
        text-align: left;
    }
    b {
        color: ${theme.colors.primary};
        font-weight: bold;
    }

    ${(props) =>
        props.isMobile && {
            flexDirection: 'column',
            paddingTop: '0',
            img: {
                width: '52px'
            }
        }}
`;

const DialogButton = styled(Button)`
    && {
        text-transform: unset;
        margin-top: 1.5em;
        &.MuiButton-contained {
            background-color: ${theme.colors.primary};
            color: #fff;
        }
        &.MuiButton-outlined {
            color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            border-radius: 3px;
        }
        svg {
            margin-right: 5px;
        }
        ${(props) =>
            !props.isMobile && {
                '&:hover': {
                    backgroundColor: theme.colors.primary,
                    color: '#fff',
                    svg: {
                        path: {
                            fill: '#fff'
                        }
                    },
                    '&.MuiButton-contained': {
                        opacity: 0.82
                    }
                }
            }}
`;
